import { useCallback } from 'react';
import type { CartItem } from '@/components/CartModal';
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency';
import { useTaxRate } from '@/contexts/StorePricingContext';
import { DEFAULT_STORE_TIMEZONE } from '@/utils/menuAvailability';
import { minMoney, subtractMoney } from '@/utils/moneyMinor';

// 지원하는 통화 타입
type Currency = 'KRW' | 'USD' | 'MXN' | 'EUR';

export interface UsePaymentCalculationsProps {
  currency: Currency;
  cartItems: CartItem[];
  selectedTip: number;
  customTip: string;
  customTipMode: 'percentage' | 'amount';
  useAllPoints: boolean;
  pointsToUse: number;
  availablePoints: number;
}

export interface UsePaymentCalculationsReturn {
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date) => string;
  formatTime: (date: Date) => string;
  /** GROSS 합계 반환 (세금 포함) */
  calculateSubtotal: () => number;
  calculateNetPrice: () => number;
  calculateTax: () => number;
  calculateTipAmount: () => number;
  calculateFinalTotal: () => number;
}

/**
 * 결제 계산 관련 로직을 담당하는 훅
 *
 * 기능:
 * - 통화별 포맷팅 (KRW, USD, MXN, EUR)
 * - 날짜/시간 포맷팅
 * - 장바구니 합계 계산 (GROSS 기준: 세금 포함 가격)
 * - 멕시코 세법 기반 세금 역산 (매장별 세율 적용)
 * - 팁 금액 계산 (퍼센트 또는 고정 금액)
 * - 포인트 할인 적용 최종 금액 계산
 *
 * @note GROSS 가격 모델: 백엔드 API는 세금 포함 가격(GROSS)을 내려줍니다.
 * @note #907: 매장별 세율을 StorePricingContext에서 가져옵니다.
 * @note 세금 역산 공식: net = gross / (1 + taxRate), tax = gross - net
 */
export function usePaymentCalculations({
  currency,
  cartItems,
  selectedTip,
  customTip,
  customTipMode,
  useAllPoints,
  pointsToUse,
  availablePoints,
}: UsePaymentCalculationsProps): UsePaymentCalculationsReturn {
  // #907: 매장별 세율 사용 (StorePricingContext에서 가져옴)
  const { taxRate } = useTaxRate();

  // 통화별 금액 포맷팅 (유틸리티 함수 래핑)
  const formatCurrency = useCallback((amount: number): string => {
    return formatCurrencyUtil(amount, currency);
  }, [currency]);

  // 날짜 포맷팅 (DD/MM/YYYY)
  // #1816: 결제 영수증 시각도 매장 타임존(멕시코)으로 — 고객 기기 TZ 의존 차단.
  const formatDate = useCallback((date: Date): string => {
    return date.toLocaleDateString('en-GB', { timeZone: DEFAULT_STORE_TIMEZONE });
  }, []);

  // 시간 포맷팅 (HH:MM:SS)
  const formatTime = useCallback((date: Date): string => {
    return date.toLocaleTimeString('en-GB', { hour12: false, timeZone: DEFAULT_STORE_TIMEZONE });
  }, []);

  // 장바구니 합계 (GROSS 기준: 세금 포함 가격)
  // 백엔드 API는 메뉴 가격을 GROSS(세금 포함)로 내려줍니다.
  const calculateSubtotal = useCallback((): number => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [cartItems]);

  // 순가격(NET) 역산: GROSS / (1 + taxRate)
  // #907: StorePricingContext에서 가져온 taxRate 사용 (기본값 0.16)
  const calculateNetPrice = useCallback((): number => {
    const gross = calculateSubtotal();
    return Math.round((gross / (1 + taxRate)) * 100) / 100;
  }, [calculateSubtotal, taxRate]);

  // 세금 역산: GROSS - NET
  // #907: StorePricingContext에서 가져온 taxRate 사용 (기본값 0.16)
  const calculateTax = useCallback((): number => {
    const gross = calculateSubtotal();
    const net = calculateNetPrice();
    return Math.round((gross - net) * 100) / 100;
  }, [calculateSubtotal, calculateNetPrice]);

  // 팁 금액 계산 (GROSS 기준 퍼센트, 소수점 2자리)
  const calculateTipAmount = useCallback((): number => {
    const gross = calculateSubtotal();

    // 선택된 팁 퍼센트가 있는 경우
    if (selectedTip > 0) {
      return Math.round(gross * (selectedTip / 100) * 100) / 100;
    }

    // 커스텀 팁이 있는 경우
    if (customTip) {
      const value = parseFloat(customTip) || 0;
      if (customTipMode === 'percentage') {
        return Math.round(gross * (value / 100) * 100) / 100;
      } else {
        return Math.round(value * 100) / 100;
      }
    }

    return 0;
  }, [calculateSubtotal, selectedTip, customTip, customTipMode]);

  // 최종 결제 금액 계산 (포인트 할인 적용)
  // 공식: GROSS + tip - pointsDiscount (세금은 GROSS에 이미 포함됨)
  const calculateFinalTotal = useCallback((): number => {
    const gross = calculateSubtotal(); // GROSS (세금 포함)
    const tip = calculateTipAmount();
    const pointsDiscount = useAllPoints
      ? minMoney(availablePoints, gross + tip)
      : pointsToUse;
    return subtractMoney(gross + tip, pointsDiscount);
  }, [calculateSubtotal, calculateTipAmount, useAllPoints, availablePoints, pointsToUse]);

  return {
    formatCurrency,
    formatDate,
    formatTime,
    calculateSubtotal,
    calculateNetPrice,
    calculateTax,
    calculateTipAmount,
    calculateFinalTotal,
  };
}
